// ============================================================================
// Hiring facility picker — tiering.
//
// BEN'S RULING, 2026-09-03. The person filling this form is an account manager
// who thinks "I need another cook for Research Medical Center". They do not
// think in customers. So the building is chosen FIRST, from every building we
// serve, and the buildings we actually staff hourly are at the top.
//
// Three tiers:
//   1. Active hospitals — 41 of the 41 hiring requests ever filed against a
//      building went to a hospital, so this is where the next one is going.
//   2. Non-hospital buildings with real hourly punch history — the three sites
//      where we demonstrably pay people by the hour but the building is not
//      typed as a hospital. Without this tier they sink into tier 3 and the
//      person filling the form has to know they exist.
//   3. Everything else still in the picker, alphabetical.
//
// ⛔ DO NOT FILTER ON A SERVICE'S BILLING BASIS to build tier 2. Two buildings
// that bill 100% hourly are stamped monthly-flat on that column, so filtering
// on it hides them. Both happen to be typed as hospitals so they land in tier 1
// anyway — the constraint still holds, it just is not load-bearing here.
//
// ⛔ NO HIDDEN "not listed" SECTION and NOTHING IS FILTERED OUT. Every row the
// picker's source view returns stays reachable in one list. That view carries
// buildings we are onboarding and buildings pending as well as active ones, and
// they stay — removing a site from a picker is how you lose the ability to act
// on it. Tier 3 therefore means "everything else in the list", not "everything
// else that is Active".
//
// ⛔ NO COPY CLAIMING A BUILDING LACKS AN HOURLY SERVICE LINE. The table that
// would support that claim has zero rows and no screen writes it, so the
// recommendation would be un-actionable.
// ============================================================================

export interface TierFacility {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
  facility_type?: string | null;
  status?: string | null;
}

export type TierKey = 'hospital' | 'hourly' | 'other';

export interface FacilityTier {
  key: TierKey;
  label: string;
  facilities: TierFacility[];
}

/**
 * The non-hospital buildings with verified hourly punch history.
 *
 * ⚠️ THIS IS HARD-CODED AND IT SHOULD NOT HAVE TO BE. The derivation is one
 * line of SQL:
 *
 *   SELECT f.facility_id FROM facilities f
 *   WHERE f.facility_type <> 'Hospital'
 *     AND EXISTS (SELECT 1 FROM shift_segments s WHERE s.facility_id = f.facility_id);
 *
 * measured 2026-09-03 and returning exactly these three. The reason the client
 * cannot run it: the punch table is readable by hr, finance and executives
 * ONLY. **An account manager — the person this form is for — cannot read it**,
 * so a client-side derivation returns nothing for them and tier 2 silently
 * collapses, which is the exact failure the tier exists to prevent. The durable
 * fix is a tiny view exposing "facility ids with punch history" to the app
 * roles, and this run was told to write no migrations, so it is reported rather
 * than written.
 *
 * `assertHourlyTierMatches` below re-derives this list for any reader who IS
 * permitted, and shouts if it has drifted. That is what stops this constant
 * going quietly stale.
 */
export const HOURLY_PUNCH_FACILITY_IDS: readonly string[] = [
  'FAC-0064', // Ascentist Doctors Hospital — typed Surgery_Center, not Hospital
  'FAC-0311', // Dallas Nephrology Associates — typed Surgery_Center
  'FAC-0328', // The Star MOB
];

const byName = (a: TierFacility, b: TierFacility) =>
  (a.facility_name ?? a.facility_id).localeCompare(b.facility_name ?? b.facility_id);

/**
 * Split the picker's facilities into the three tiers, alphabetical within each.
 *
 * Every input facility appears in exactly one tier and none is dropped — that
 * is asserted by the tests, because "reachable in one list" is the point of the
 * ruling and an ordering change that loses a row is worse than no change.
 *
 * @param hourlyIds the tier-2 ids. Defaults to the constant above; pass a
 *                  derived set where the caller is permitted to read one.
 */
export function tierFacilities(
  facilities: readonly TierFacility[],
  hourlyIds: readonly string[] = HOURLY_PUNCH_FACILITY_IDS,
): FacilityTier[] {
  const hourly = new Set(hourlyIds);
  const t1: TierFacility[] = [];
  const t2: TierFacility[] = [];
  const t3: TierFacility[] = [];

  for (const f of facilities) {
    // Tier 2 is checked FIRST. A building on the hourly list that also happens
    // to be typed as a hospital belongs in tier 1 — it is already at the top —
    // so hospitals win the tie and the hourly tier stays what it is for: the
    // sites you would otherwise not think to look for.
    if (f.facility_type === 'Hospital') t1.push(f);
    else if (hourly.has(f.facility_id)) t2.push(f);
    else t3.push(f);
  }

  return [
    { key: 'hospital', label: 'Hospitals', facilities: t1.sort(byName) },
    { key: 'hourly', label: 'Other sites we staff hourly', facilities: t2.sort(byName) },
    { key: 'other', label: 'All other buildings', facilities: t3.sort(byName) },
  ];
}

/** Case-insensitive match on name or id, so a search finds a building either way. */
export function facilityMatches(f: TierFacility, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    (f.facility_name ?? '').toLowerCase().includes(q) ||
    f.facility_id.toLowerCase().includes(q)
  );
}

/** Apply a search across every tier, dropping tiers left empty by it. */
export function searchTiers(tiers: readonly FacilityTier[], query: string): FacilityTier[] {
  if (!query.trim()) return tiers as FacilityTier[];
  return tiers
    .map((t) => ({ ...t, facilities: t.facilities.filter((f) => facilityMatches(f, query)) }))
    .filter((t) => t.facilities.length > 0);
}

/**
 * Compare the hard-coded tier-2 list against a freshly derived one and report
 * any drift. Called only where the reader is permitted to see punch history;
 * a permitted reader who finds a difference is the signal that the constant
 * needs replacing. Returns the message rather than logging, so it is testable.
 */
export function assertHourlyTierMatches(derivedIds: readonly string[]): string | null {
  const expected = new Set(HOURLY_PUNCH_FACILITY_IDS);
  const actual = new Set(derivedIds);
  const missing = [...actual].filter((id) => !expected.has(id)).sort();
  const stale = [...expected].filter((id) => !actual.has(id)).sort();
  if (missing.length === 0 && stale.length === 0) return null;
  const parts: string[] = [];
  if (missing.length > 0) parts.push(`now has punch history and is not listed: ${missing.join(', ')}`);
  if (stale.length > 0) parts.push(`is listed but has no punch history: ${stale.join(', ')}`);
  return `Hiring picker hourly tier has drifted — ${parts.join('; ')}. Update HOURLY_PUNCH_FACILITY_IDS.`;
}
